import { format } from "date-fns";
import { parseCalendarDate } from "./helpers";

export interface AvailabilityDateRange {
  start: Date;
  end: Date;
}

const STATUS_PRIORITY: Record<string, number> = {
  booked: 3,
  unavailable: 2,
  available: 1,
};

export interface AvailabilityCalendarDay {
  date: string;
  status: string;
  price?: number | null;
  amount?: number | null;
  blocked?: boolean;
}

export interface AvailabilityCalendarSegment {
  startDate: string;
  endDate: string;
  status: string;
  price?: number;
}

export interface ParsedAvailabilityCalendar {
  priceMap: Record<string, number>;
  unavailableDates: string[];
  statusMap: Record<string, string>;
  surgeDays: { date: string; price: number }[];
  surgeDateRanges: AvailabilityDateRange[];
}

export function getAvailabilityFetchRange() {
  const currentDate = new Date();
  const nextYearDate = new Date();
  nextYearDate.setFullYear(currentDate.getFullYear() + 1);

  return {
    startDate: format(currentDate, "yyyy-MM-01"),
    endDate: format(nextYearDate, "yyyy-MM-01"),
  };
}

function buildSurgeDateRangesFromSegments(
  calendar: AvailabilityCalendarSegment[] | undefined,
): AvailabilityDateRange[] {
  if (!Array.isArray(calendar)) {
    return [];
  }

  return calendar
    .filter((item) => item.price && item.status === "available")
    .map((item) => ({
      start: parseCalendarDate(item.startDate),
      end: parseCalendarDate(item.endDate),
    }));
}

function parseFromCalendarSegments(
  calendar: AvailabilityCalendarSegment[],
  defaultPrice: number,
): Omit<ParsedAvailabilityCalendar, "surgeDateRanges"> {
  const priceMap: Record<string, number> = {};
  const unavailableDates: string[] = [];
  const statusMap: Record<string, string> = {};
  const surgeDays: { date: string; price: number }[] = [];
  const dayMap: Record<string, { status: string; price: number }> = {};

  calendar.forEach((item) => {
    let currentDate = parseCalendarDate(item.startDate);
    const segmentEnd = parseCalendarDate(item.endDate);
    segmentEnd.setDate(segmentEnd.getDate() - 1);

    while (currentDate <= segmentEnd) {
      const formattedDate = format(currentDate, "yyyy-MM-dd");
      const itemPriority = STATUS_PRIORITY[item.status] ?? 0;
      const existing = dayMap[formattedDate];
      const existingPriority = existing
        ? (STATUS_PRIORITY[existing.status] ?? 0)
        : 0;

      if (!existing || itemPriority >= existingPriority) {
        dayMap[formattedDate] = {
          status: item.status,
          price: item.price ?? defaultPrice,
        };
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  Object.entries(dayMap).forEach(([date, { status, price }]) => {
    priceMap[date] = price;

    if (status === "unavailable" || status === "booked") {
      unavailableDates.push(date);
      statusMap[date] = status;
    }

    if (status === "available" && price !== defaultPrice) {
      surgeDays.push({ date, price });
    }
  });

  return { priceMap, unavailableDates, statusMap, surgeDays };
}

export function parseAvailabilityCalendarResponse(
  data: {
    days?: AvailabilityCalendarDay[];
    calendar?: AvailabilityCalendarSegment[];
    defaultPrice?: number | null;
  },
  fallbackPrice: number,
): ParsedAvailabilityCalendar {
  const defaultPrice = data.defaultPrice ?? fallbackPrice;
  const surgeDateRanges = buildSurgeDateRangesFromSegments(data.calendar);

  if (Array.isArray(data.days) && data.days.length > 0) {
    const priceMap: Record<string, number> = {};
    const unavailableDates: string[] = [];
    const statusMap: Record<string, string> = {};
    const surgeDays: { date: string; price: number }[] = [];

    data.days.forEach((day) => {
      const dateKey = day.date.split("T")[0];
      const price = day.price ?? day.amount ?? defaultPrice;
      priceMap[dateKey] = price;

      if (
        day.status === "unavailable" ||
        day.status === "booked" ||
        day.blocked
      ) {
        unavailableDates.push(dateKey);
        statusMap[dateKey] = day.status;
      }

      if (
        day.status === "available" &&
        day.price != null &&
        day.price !== defaultPrice
      ) {
        surgeDays.push({ date: dateKey, price: day.price });
      }
    });

    return {
      priceMap,
      unavailableDates,
      statusMap,
      surgeDays,
      surgeDateRanges,
    };
  }

  const fromSegments = parseFromCalendarSegments(data.calendar || [], defaultPrice);

  return {
    ...fromSegments,
    surgeDateRanges:
      surgeDateRanges.length > 0
        ? surgeDateRanges
        : buildSurgeDateRangesFromSegments(data.calendar),
  };
}
